/**
 * The shared gate for ops that overwrite an EXISTING structure with an
 * already-computed geometry, exactly like their toolbar buttons
 * (`roi.replaceTexture` — fill cavities, remove small parts). The in-place
 * twin of {@link gateProposal}: hover preview of the computed geometry,
 * adopt-or-release Apply/Reject, and the Apply-time refusals that matter
 * when the source itself is overwritten:
 *  - the active structure set changed,
 *  - the structure became approved,
 *  - the structure was edited after the preview (its edit version moved) —
 *    writing the stale preview would silently discard those edits.
 * Every refusal frees the unapplied geometry exactly once. Pure/React-free.
 */
import type { InPlaceEditOutcome } from '../../contract';
import {
    makeHandleGuard,
    reasonOf,
    type ComputedGeometry,
    type GeometryPreviewPort,
} from './pipeline';

/** The ports an in-place op needs on top of its own engine call. */
export interface InPlaceEditDeps {
    /** False for an approved structure (the toolbar refuses those too). */
    canModify(roiNumber: number): boolean;
    /** Identity of the active structure set, read live. */
    currentContextId(): string | undefined;
    /** The structure's edit version (moves on every edit, undo and redo);
     *  undefined when the structure is gone. */
    editVersion(roiNumber: number): number | undefined;
    readonly geometry: {
        /** Free a computed geometry that will not be applied. */
        releaseComputed?(handle: unknown): void;
    };
    readonly commit: {
        /** Replace the structure's contours with the computed geometry, as
         *  the toolbar does; returns the new volume (cm³). */
        replaceInPlace(roiNumber: number, computed: ComputedGeometry): number;
    };
    /** Hover overlay of the computed result (absent in unit fakes). */
    readonly preview?: GeometryPreviewPort<ComputedGeometry>;
}

/** What the op captured BEFORE computing (so a racing edit is caught). */
export interface InPlaceTarget {
    readonly roiNumber: number;
    readonly name: string;
    readonly volumeBeforeCm3: number;
    readonly plannedVersion: number | undefined;
    readonly plannedContextId: string | undefined;
}

export function notModifiableReason(name: string): string {
    return `'${name}' is approved and cannot be modified`;
}

export function gateInPlaceProposal(
    computed: ComputedGeometry,
    target: InPlaceTarget,
    deps: InPlaceEditDeps
): {
    showPreview(): void;
    hidePreview(): void;
    reject(): InPlaceEditOutcome;
    apply(): Promise<InPlaceEditOutcome>;
} {
    const guard = makeHandleGuard(computed, deps);
    const refuse = (reason: string): InPlaceEditOutcome => {
        guard.reject();
        return { status: 'failed', reason };
    };
    const { roiNumber, name, volumeBeforeCm3 } = target;

    return {
        showPreview: () => deps.preview?.show(computed),
        hidePreview: () => deps.preview?.hide(),
        reject: () => guard.reject(),
        apply: async () => {
            if (deps.currentContextId() !== target.plannedContextId) {
                return refuse(
                    'the active structure set changed before this was ' +
                        'applied; re-issue the request against the current set'
                );
            }
            if (!deps.canModify(roiNumber)) {
                return refuse(notModifiableReason(name));
            }
            if (deps.editVersion(roiNumber) !== target.plannedVersion) {
                return refuse(
                    `'${name}' was edited after the preview; ask again so ` +
                        'the result is computed from its current contours'
                );
            }
            guard.claim();
            let volumeAfterCm3: number;
            try {
                volumeAfterCm3 = deps.commit.replaceInPlace(
                    roiNumber,
                    computed
                );
            } catch (err) {
                // Not adopted by the structure: free it here.
                deps.geometry.releaseComputed?.(computed.handle);
                return { status: 'failed', reason: reasonOf(err) };
            }
            return {
                status: 'applied',
                roiNumber,
                roi: name,
                volumeCm3: volumeAfterCm3,
                volumeDeltaCm3: volumeAfterCm3 - volumeBeforeCm3,
            };
        },
    };
}
